import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Coffee, Gamepad2, LogOut, Shield, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { Input } from "@/components/ui/input";
import { ParticleField } from "@/components/ParticleField";

const JAVA_RE = /^[A-Za-z0-9_]{3,16}$/;
const BEDROCK_RE = /^[A-Za-z0-9_ ]{1,15}$/;

export default function AccountPage() {
  const { username, setUsername, platform, setPlatform, user, logoutUser } = useStore();
  const navigate = useNavigate();
  const [ign, setIgn] = useState(username);
  const [error, setError] = useState("");

  const save = () => {
    const trimmed = ign.trim();
    const ok = platform === "bedrock" ? BEDROCK_RE.test(trimmed) : JAVA_RE.test(trimmed);
    if (!ok) {
      setError(platform === "bedrock"
        ? "Enter a valid Bedrock gamertag (1-15 letters, numbers, spaces or _)"
        : "Enter a valid Minecraft username (3-16 letters, numbers or _)");
      return;
    }
    setUsername(trimmed);
    toast.success("IGN saved", {
      description: platform === "bedrock" ? `Purchases will deliver to ${trimmed} (Bedrock)` : `Purchases will deliver to ${trimmed}`,
    });
    navigate("/");
  };

  return (
    <main data-testid="account-page" className="relative min-h-screen bg-[#05030A] flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 45% at 50% 35%, rgba(139,92,246,0.18), transparent 70%)" }} />
      <ParticleField className="absolute inset-0" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm pt-16"
      >
        <Link
          to="/"
          data-testid="account-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition-colors duration-300 mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to store
        </Link>

        <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/90 backdrop-blur-xl p-8">
          <div className="flex items-center gap-2 justify-center">
            <Shield className="w-6 h-6 text-[#8B5CF6]" strokeWidth={2.5} />
            <span className="font-display text-2xl tracking-tight text-white">ULTI<span className="text-[#A78BFA]">SMP</span></span>
          </div>
          <p className="mt-2 text-center text-xs tracking-[0.3em] text-slate-500">YOUR ACCOUNT</p>

          <h1 className="mt-8 font-display text-3xl tracking-tight text-white text-center">Just your IGN</h1>
          <p className="mt-2 text-sm text-slate-400 text-center leading-relaxed">
            That's all we need. Every purchase delivers straight to this Minecraft username.
          </p>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-2" data-testid="platform-toggle">
              {[
                { value: "java", label: "JAVA", icon: Coffee },
                { value: "bedrock", label: "BEDROCK", icon: Smartphone },
              ].map((p) => {
                const on = platform === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => { setPlatform(p.value); setError(""); }}
                    data-testid={`platform-${p.value}`}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold tracking-[0.15em] border transition-[background-color,border-color,color] duration-300 ${
                      on
                        ? "bg-[#8B5CF6] border-[#8B5CF6] text-white"
                        : "bg-white/5 border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    <p.icon className="w-3.5 h-3.5" /> {p.label}
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={ign}
                onChange={(e) => { setIgn(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && save()}
                placeholder={platform === "bedrock" ? "e.g. Steve 2024" : "e.g. Steve_2024"}
                data-testid="account-ign-input"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
              />
            </div>
            {platform === "bedrock" && (
              <p className="text-[11px] text-slate-500" data-testid="bedrock-hint">
                Bedrock deliveries use the server prefix — Tebex will see <span className="text-[#A78BFA] font-semibold">.{(ign.trim() || "YourName").replace(/ /g, "_")}</span>
              </p>
            )}
            {error && <p className="text-sm text-red-400" data-testid="account-ign-error">{error}</p>}
            <button
              onClick={save}
              data-testid="account-save-button"
              className="w-full py-3.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] transition-[background-color] duration-300 flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" /> SAVE IGN
            </button>
          </div>

          {username && (
            <p className="mt-4 text-center text-xs text-slate-500" data-testid="account-current-ign">
              Saved: <span className="text-[#A78BFA] font-semibold">{username}</span>
            </p>
          )}

          {user && (
            <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between" data-testid="account-staff-info">
              <span className="text-xs text-slate-400">
                Logged in as <span className="text-white font-semibold">{user.ign || user.email}</span>
                <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-widest bg-emerald-400/20 text-emerald-300">{user.role.toUpperCase()}</span>
              </span>
              <button onClick={logoutUser} data-testid="account-logout-button" className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200" aria-label="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link
              to="/account"
              data-testid="account-staff-login-link"
              className="text-[11px] tracking-[0.2em] text-slate-600 hover:text-[#A78BFA] transition-colors duration-300"
            >
              MEDIA / STAFF LOGIN →
            </Link>
          </div>
        </div>
      </motion.div>
    </main>
  );
}
